import type { UseFormReturn } from 'react-hook-form';

import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { match } from 'ts-pattern';

import { useUser } from '~/api/user';
import { Button, ExternalLink, Infotip } from '~/components';
import { Field, Select } from '~/components/Form';
import { AnchorTargets, SearchParamKeys, SearchParamModals } from '~/constants/url';
import { routes } from '~/providers/RouterProvider/router.routes';
import { qs } from '~/utils/searchParams';
import { generateEntryNumber } from '~/utils/string';

import { addSeriesButtonStyle, descriptionRowStyle } from './FormNumberingSeries.css';

type FormWithNumberingSeriesId = {
  numberingSeriesId: number | null;
};

type Props<T extends FormWithNumberingSeriesId> = {
  enabledFor: 'invoices' | 'creditNotes';
  form: T extends FormWithNumberingSeriesId ? UseFormReturn<T> : never;
  required?: boolean;
  showHelpText?: boolean;
};

export const FormNumberingSeries = <T extends FormWithNumberingSeriesId>({
  enabledFor,
  form: { control, watch },
  required = true,
  showHelpText = true,
}: Props<T>) => {
  const { data: user } = useUser();

  const selectedNumberingSeries = user.numberingSeries.find((numberingSeries) => numberingSeries.id === watch('numberingSeriesId')) ?? null;

  const navigate = useNavigate();
  const { t } = useTranslation(['creditnotes', 'invoices', 'settings', 'validation']);

  const numberingSeriesOptions = useMemo(
    () =>
      user.numberingSeries
        .filter((numberingSeries) =>
          match(enabledFor)
            .with('invoices', () => numberingSeries.enabledForInvoices)
            .with('creditNotes', () => numberingSeries.enabledForCreditNotes)
            .exhaustive(),
        )
        .map((numberingSeries) => ({ value: numberingSeries.id, label: numberingSeries.name })),
    [enabledFor, user.numberingSeries],
  );

  const selectItems = useMemo(
    () =>
      required ? numberingSeriesOptions : [{ value: null, label: t('invoices:fields.numberingSeriesId.none') }, ...numberingSeriesOptions],
    [numberingSeriesOptions, required, t],
  );

  return (
    <Controller
      control={control}
      name="numberingSeriesId"
      render={({ field: { name, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label
            infotip={
              <Trans
                components={{ a: <ExternalLink /> }}
                i18nKey={
                  numberingSeriesOptions.length > 0
                    ? 'invoices:fields.numberingSeriesId.labelTooltip'
                    : 'invoices:fields.numberingSeriesId.tooltipCreate'
                }
              />
            }
            optional={!required}
          >
            {t('invoices:fields.numberingSeriesId.label')}
          </Field.Label>

          <Select.Root items={selectItems} onValueChange={onChange} value={value}>
            <Select.Button placeholder={t('invoices:fields.numberingSeriesId.placeholder')} ref={ref} />
            <Select.List>
              {selectItems.map((option) => (
                <Select.Item key={option.value ?? 'none'} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.List>
          </Select.Root>

          <Button
            className={addSeriesButtonStyle}
            emphasis="tertiary"
            icon="Add"
            onClick={() =>
              navigate({
                pathname: routes.settingsInvoice,
                search: qs({ [SearchParamKeys.OPEN_MODAL]: SearchParamModals.ADD_NUMBERING_SERIES }),
                hash: AnchorTargets.SETTINGS_INVOICE_NUMBERING_SERIES,
              })
            }
          >
            {t('settings:general.numberingSeries.add')}
          </Button>

          <Field.Footer>
            {showHelpText && selectedNumberingSeries && (
              <div className={descriptionRowStyle}>
                <Field.Description>
                  <Trans
                    components={[<strong key={null} />]}
                    i18nKey={match(enabledFor)
                      .with('invoices', () => 'invoices:fields.numberingSeriesId.helpText' as const)
                      .with('creditNotes', () => 'creditnotes:fields.numberingSeriesId.helpText' as const)
                      .exhaustive()}
                    values={{ entryNumber: generateEntryNumber(selectedNumberingSeries) }}
                  />
                </Field.Description>

                <Infotip>
                  {match(enabledFor)
                    .with('invoices', () => t('invoices:fields.numberingSeriesId.tooltip'))
                    .with('creditNotes', () => t('creditnotes:fields.numberingSeriesId.tooltip'))
                    .exhaustive()}
                </Infotip>
              </div>
            )}

            <Field.Error match={!!error}>{error?.message}</Field.Error>
          </Field.Footer>
        </Field.Root>
      )}
      rules={{ required: { value: required, message: t('validation:required') } }}
    />
  );
};
